import { useEffect, useMemo, useState } from 'react';
import { getHomeFullData } from '../services/homeService';
import mockData from '../mock';

const defaultOptions = {
  enabled: true,
  useMockOnError: true
};

export function useHomeData(options) {
  const { enabled, useMockOnError } = useMemo(
    () => ({ ...defaultOptions, ...options }),
    [options]
  );

  const [data, setData] = useState(useMockOnError ? mockData : null);
  const [isLoading, setIsLoading] = useState(Boolean(enabled));
  const [error, setError] = useState(null);
  const [isUsingFallback, setIsUsingFallback] = useState(Boolean(useMockOnError));

  useEffect(() => {
    if (!enabled) {
      setIsLoading(false);
      return;
    }

    let isMounted = true;
    let fetchTimer;
    let idleCallbackId;

    async function fetchData() {
      setIsLoading(true);
      try {
        const apiData = await getHomeFullData();
        if (!isMounted) {
          return;
        }
        setData(apiData);
        setError(null);
        setIsUsingFallback(false);
      } catch (err) {
        if (!isMounted) {
          return;
        }
        setError(err);
        if (useMockOnError) {
          setData((current) => current ?? mockData);
          setIsUsingFallback(true);
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    }

    const scheduleFetch = () => {
      if ('requestIdleCallback' in window) {
        idleCallbackId = window.requestIdleCallback(fetchData, { timeout: 1500 });
      } else {
        fetchTimer = window.setTimeout(fetchData, 300);
      }
    };

    if (document.readyState === 'complete') {
      scheduleFetch();
    } else {
      window.addEventListener('load', scheduleFetch, { once: true });
    }

    return () => {
      isMounted = false;
      window.removeEventListener('load', scheduleFetch);
      window.clearTimeout(fetchTimer);
      if (idleCallbackId && 'cancelIdleCallback' in window) {
        window.cancelIdleCallback(idleCallbackId);
      }
    };
  }, [enabled, useMockOnError]);

  return {
    data,
    isLoading,
    error,
    isUsingFallback
  };
}

export default useHomeData;

